import { Text, Tooltip } from "@mantine/core";
import { DataTable } from "@xplatform/design-system";
import type { DataTableColumn } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { SearchUsageViewModel } from "@models/monitoring";
import { formatCurrency, formatDate } from "@utils/formatters";
import "./SearchUsageTable.scss";

interface SearchUsageTableProps {
    entries: SearchUsageViewModel[];
}

export const SearchUsageTable = ({ entries }: SearchUsageTableProps) => {
    const { t } = useTranslation();

    const columns: DataTableColumn<SearchUsageViewModel>[] = [
        {
            key: "requestId",
            header: t("admin.monitoring.labels.requestId"),
            width: "12%",
            render: (entry) => (
                <Tooltip label={entry.requestId} position="top">
                    <Text size="sm" className="search-usage-table__id">
                        {entry.requestIdShort}
                    </Text>
                </Tooltip>
            ),
        },
        {
            key: "createdBy",
            header: t("admin.monitoring.labels.createdBy"),
            width: "20%",
            render: (entry) => (
                <Text size="sm" c="dimmed" truncate="end">
                    {entry.createdBy || "-"}
                </Text>
            ),
        },
        {
            key: "used",
            header: t("admin.monitoring.labels.used"),
            width: "22%",
            render: (entry) => (
                <Text size="sm" c="dimmed">
                    {formatDate(entry.usedAt)}
                </Text>
            ),
        },
        {
            key: "organisation",
            header: t("admin.monitoring.labels.organisation"),
            width: "22%",
            render: (entry) => (
                <Text size="sm" c="dimmed" truncate="end">
                    {entry.organisationName || "-"}
                </Text>
            ),
        },
        {
            key: "events",
            header: t("admin.monitoring.labels.events"),
            width: "12%",
            align: "right",
            render: (entry) => <Text size="sm">{entry.eventCount.toLocaleString()}</Text>,
        },
        {
            key: "totalCost",
            header: t("admin.monitoring.labels.totalCost"),
            width: "12%",
            align: "right",
            render: (entry) => (
                <Text size="sm" fw={600}>
                    {formatCurrency(entry.totalCost, entry.currency)}
                </Text>
            ),
        },
    ];

    return (
        <DataTable<SearchUsageViewModel>
            className="search-usage-table"
            fz="sm"
            verticalSpacing="xs"
            horizontalSpacing="md"
            columns={columns}
            data={entries}
            getRowKey={(entry) => entry.requestId}
        />
    );
};
